import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * @type {ResizeObserver | null}
   */
  #openResizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    document.addEventListener('click', this.#handleDocumentClick, { capture: true });
    document.addEventListener('keydown', this.#handleDocumentKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.removeEventListener('click', this.#handleDocumentClick, { capture: true });
    document.removeEventListener('keydown', this.#handleDocumentKeydown);
    this.#cleanupMutationObserver();
    this.#openResizeObserver?.disconnect();
    this.#openResizeObserver = null;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #handleDocumentClick = (event) => {
    if (!this.#state.activeItem || !(event.target instanceof Node)) return;

    const path = event.composedPath();
    if (path.includes(this)) return;
    if (this.overflowMenu && path.includes(this.overflowMenu)) return;

    this.#deactivate(this.#state.activeItem, { force: true });
  };

  #handleDocumentKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#state.activeItem) return;

    this.#deactivate(this.#state.activeItem, { force: true });
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Toggle the selected menu item open or closed
   * @param {PointerEvent | KeyboardEvent} event
   */
  toggle = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    const hasSubmenu = Boolean(findSubmenu(item)) || event.target.closest('[slot="more"]');
    if (!hasSubmenu) return;

    event.preventDefault();

    if (item === this.#state.activeItem) {
      this.#deactivate(item, { force: true });
      return;
    }

    this.activate(event);
  };

  /**
   * Handle keyboard interaction for menu items with submenus
   * @param {KeyboardEvent} event
   */
  handleMenuKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    this.toggle(event);
  };

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent | KeyboardEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const measureOptions = { isDefaultSlot, hasSubmenu };

    if (submenu) {
      // Mark submenu as active so content-visibility stops using the 500px intrinsic size.
      submenu.dataset.active = '';
      this.#cleanupMutationObserver();
      this.#watchOpenHeight(submenu, measureOptions);
      this.#applyOpenHeight(submenu, measureOptions);

      // The first read can still be the intrinsic size. Measure again after layout.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.#applyOpenHeight(submenu, measureOptions);
        });
      });
    } else {
      this.#openResizeObserver?.disconnect();
      this.headerComponent.style.setProperty('--submenu-height', '0px');
      this.#setFullOpenHeaderHeight(0);
    }

    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Read the open submenu height. Overflow items need the list height, not the nested submenu box.
   * @param {HTMLElement | null | undefined} submenu
   * @param {{ isDefaultSlot: boolean, hasSubmenu: boolean, stable?: boolean }} options
   */
  #measureOpenHeight(submenu, { isDefaultSlot, hasSubmenu, stable = false }) {
    if (!submenu) return 0;

    if (isDefaultSlot || stable) {
      if (isDefaultSlot) return submenu.offsetHeight || 0;

      return Math.max(submenu.offsetHeight || 0, this.overflowMenu?.offsetHeight || 0);
    }

    const overflowListHeight = this.#getOverflowListLinksHeight();

    if (!hasSubmenu) return overflowListHeight;

    const overflowHeight = this.overflowMenu?.offsetHeight || 0;

    return Math.max(overflowHeight, overflowListHeight);
  }

  /**
   * Keep the clip and the underlay on the same height. Updating only one of them cuts the panel short.
   * @param {HTMLElement | null | undefined} submenu
   * @param {{ isDefaultSlot: boolean, hasSubmenu: boolean, stable?: boolean }} options
   */
  #applyOpenHeight(submenu, options) {
    if (!this.headerComponent || this.#state.activeItem == null) return;

    const finalHeight = this.#measureOpenHeight(submenu, options);

    if (finalHeight <= 0) return;

    const next = `${finalHeight}px`;
    const current = this.headerComponent.style.getPropertyValue('--submenu-height').trim();

    if (current === next) return;

    this.headerComponent.style.setProperty('--submenu-height', next);
    this.#setFullOpenHeaderHeight(finalHeight);
  }

  /**
   * @param {HTMLElement} submenu
   * @param {{ isDefaultSlot: boolean, hasSubmenu: boolean }} options
   */
  #watchOpenHeight(submenu, options) {
    this.#openResizeObserver?.disconnect();

    const target = options.isDefaultSlot ? submenu : this.overflowMenu ?? submenu;

    this.#openResizeObserver = new ResizeObserver(() => {
      this.#applyOpenHeight(submenu, { ...options, stable: true });
    });
    this.#openResizeObserver.observe(target);
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem, { force = false } = {}) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    this.#openResizeObserver?.disconnect();
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when activating the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  const menuitem = element.closest('[ref="menuitem"]') ?? element.querySelector('[ref="menuitem"]');
  return menuitem instanceof HTMLElement ? menuitem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
